import { useCallback } from "react";

import { useHistory } from "react-router";
import { CLOUD_FLOW_CREATION_STATUS, type CloudflowCreationStatus } from "@doitintl/cmp-models";
import { type ModelTimestamp } from "@doitintl/models-any";
import { type ChipProps, Link, Stack, TableCell } from "@mui/material";
import Typography from "@mui/material/Typography";
import camelCase from "lodash/camelCase";
import { DateTime } from "luxon";

import { cloudflowTexts } from "../../../assets/texts";
import useRouteMatchURL from "../../../Components/hooks/useRouteMatchURL";
import { ThreeDotsMenu, type ThreeDotsMenuOption } from "../../../Components/ThreeDotsMenu";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { isCustomerInPresentationMode } from "../../../Context/useCustomerOrPresentationModeCustomer";
import { type CloudflowWSnap } from "../../../types/Cloudflow";
import mixpanel from "../../../utils/mixpanel";
import { useToggle } from "../../../utils/useToggle";
import { StatusChip } from "../CloudflowBuilder/Topbar/StatusChip";
import { useDuplicateCloudflow, useRunCloudflow } from "../hooks";
import { useEditLink } from "./utils";

export type CloudflowRowProps = {
  row: CloudflowWSnap;
  actions: {
    onViewRunHistory: (row?: CloudflowWSnap) => void;
    onDelete: (id: string) => void;
  };
};

export const getStatusChipProps = (status: CloudflowCreationStatus): Pick<ChipProps, "color" | "label"> => {
  switch (status) {
    case CLOUD_FLOW_CREATION_STATUS.PUBLISHED:
    case CLOUD_FLOW_CREATION_STATUS.NEW:
      return { color: "success", label: status };
    case CLOUD_FLOW_CREATION_STATUS.DRAFT:
      return { color: "default", label: status };
    default:
      return { color: "default", label: "Loading..." };
  }
};

const CloudflowRow = ({ row, actions: { onDelete, onViewRunHistory } }: CloudflowRowProps) => {
  const routeMatchURL = useRouteMatchURL();
  const history = useHistory();
  const { customer } = useCustomerContext();
  const [duplicateCloudflow, duplicateCloudflowLoading] = useDuplicateCloudflow();
  const [queriesEnabled, , , toggleQueriesEnabled] = useToggle(false);
  const {
    runCloudflow,
    runLoading,
    disabled: runDisabled,
    disabledReason,
  } = useRunCloudflow(customer.id, row.ref.id, row.data.status, queriesEnabled);

  const onEditLink = useEditLink();

  const onEdit = useCallback(
    (id: string) => {
      const link = onEditLink(id);
      history.push(link);
    },
    [history, onEditLink]
  );

  const onNameClick = useCallback(
    (link: string) => {
      history.push(link);
    },
    [history]
  );

  const menuOptions: ThreeDotsMenuOption[] = [
    {
      label: cloudflowTexts.VIEW_RUN_HISTORY,
      key: camelCase(cloudflowTexts.VIEW_RUN_HISTORY),
      action: () => {
        onViewRunHistory(row);
      },
      dataCy: "viewRunHistory",
    },
    {
      label: "Run",
      key: "Run",
      action: () => {
        runCloudflow?.();
      },
      disabled: runLoading || runDisabled,
      tooltip: disabledReason,
    },
    {
      label: cloudflowTexts.EDIT_CLOUDFLOW,
      key: camelCase(cloudflowTexts.EDIT_CLOUDFLOW),
      action: () => {
        onEdit(row.ref.id);
      },
      dataCy: "editCloudflow",
    },
    ...(!isCustomerInPresentationMode(customer)
      ? [
          {
            label: duplicateCloudflowLoading ? <Typography>Duplicating...</Typography> : cloudflowTexts.DUPLICATE,
            key: camelCase(cloudflowTexts.DUPLICATE),
            action: () => {
              duplicateCloudflow(customer.id, row.ref.id);
              mixpanel.track("cloudflow.create");
            },
            dataCy: "duplicateCloudflow",
            disabled: duplicateCloudflowLoading,
          },
        ]
      : []),
    {
      label: <Typography color="error">{cloudflowTexts.DELETE}</Typography>,
      key: camelCase(cloudflowTexts.DELETE),
      action: () => {
        onDelete(row.ref.id);
      },
      dataCy: "deleteCloudflow",
    },
  ];

  const linkDestination = `${routeMatchURL}/edit/${row.ref.id}`;

  return (
    <>
      <TableCell>
        <Link
          style={{ cursor: "pointer" }}
          onClick={() => {
            onNameClick(linkDestination);
          }}
          color="inherit"
          variant="body2"
        >
          {row.data.name}
        </Link>
      </TableCell>
      <TableCell>{row.data.owner}</TableCell>
      <TableCell>
        <StatusChip status={row.data.status} />
      </TableCell>
      <TableCell>{formatLastUpdatedTime(row.data.lastExecuted)}</TableCell>
      <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
        {formatLastUpdatedTime(row.data.updatedAt)}
      </TableCell>
      <TableCell padding="none">
        <Stack
          sx={{
            alignItems: "end",
          }}
        >
          <ThreeDotsMenu options={menuOptions} onClick={toggleQueriesEnabled} />
        </Stack>
      </TableCell>
    </>
  );
};

function formatLastUpdatedTime(time: ModelTimestamp | undefined) {
  if (!time) {
    return "";
  }

  const date = DateTime.fromJSDate(time.toDate());
  return date.isValid ? date.toLocaleString(DateTime.DATETIME_MED) : "";
}
export default CloudflowRow;
